import React, { useState, useEffect } from 'react';
import { GuidingArrowProps, ArrowPosition } from './types';

const GuidingArrow: React.FC<GuidingArrowProps> = ({
  target,
  direction = 'left',
  curved = true
}) => {
  const [position, setPosition] = useState<ArrowPosition>({
    x: 0,
    y: 0,
    targetX: 0,
    targetY: 0,
  });
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const calculatePosition = () => {
      const targetEl = document.querySelector(`[data-onboarding-target="${target}"]`);
      const modalEl = document.querySelector('.onboarding-modal');

      if (!targetEl || !modalEl) {
        setIsVisible(false);
        return;
      }

      const targetRect = targetEl.getBoundingClientRect();
      const modalRect = modalEl.getBoundingClientRect();

      // Only show arrow on larger screens (desktop and tablet)
      const screenWidth = window.innerWidth;
      if (screenWidth < 768) {
        setIsVisible(false);
        return;
      }

      setIsVisible(true);

      // Calculate arrow start point (from modal edge)
      let startX = modalRect.left;
      let startY = modalRect.top + modalRect.height / 2;

      // Calculate arrow end point (to target) - account for arrowhead size
      let endX = targetRect.right + 10;
      let endY = targetRect.top + targetRect.height / 2;

      // Adjust based on direction
      if (direction === 'right') {
        startX = modalRect.right;
        endX = targetRect.left - 10;
      } else if (direction === 'up') {
        startX = modalRect.left + modalRect.width / 2;
        startY = modalRect.bottom;
        endX = targetRect.left + targetRect.width / 2;
        endY = targetRect.top - 10;
      } else if (direction === 'down') {
        startX = modalRect.left + modalRect.width / 2;
        startY = modalRect.top;
        endX = targetRect.left + targetRect.width / 2;
        endY = targetRect.bottom + 10;
      }

      setPosition({
        x: startX,
        y: startY,
        targetX: endX,
        targetY: endY,
      });
    };

    // Calculate position on mount and window resize
    calculatePosition();
    window.addEventListener('resize', calculatePosition);

    // Recalculate when DOM changes (for dynamic content)
    const observer = new MutationObserver(calculatePosition);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true
    });

    return () => {
      window.removeEventListener('resize', calculatePosition);
      observer.disconnect();
    };
  }, [target, direction]);

  if (!isVisible) {
    return null;
  }

  // Calculate control point for curved arrow
  const midX = (position.x + position.targetX) / 2;
  const midY = (position.y + position.targetY) / 2;

  // Create a gentle curve by offsetting the control point
  const controlX = midX;
  const controlY = midY - 30; // Gentle upward curve

  // Path for the arrow
  const path = curved
    ? `M ${position.x} ${position.y} Q ${controlX} ${controlY} ${position.targetX} ${position.targetY}`
    : `M ${position.x} ${position.y} L ${position.targetX} ${position.targetY}`;

  return (
    <svg
      className="absolute guiding-arrow pointer-events-none"
      style={{
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 9998,
        position: 'fixed',
      }}
      aria-hidden="true"
    >
      <defs>
        <marker
          id={`arrowhead-${target}`}
          markerWidth="10"
          markerHeight="10"
          refX="1"
          refY="5"
          orient="auto"
        >
          <polygon
            points="0 0, 10 5, 0 10"
            fill="#0a66c2"
            className="arrow-tip"
          />
        </marker>
      </defs>

      {/* Arrow path */}
      <path
        d={path}
        stroke="#0a66c2"
        strokeWidth="3"
        fill="none"
        markerEnd={`url(#arrowhead-${target})`}
        className="arrow-path"
      />
    </svg>
  );
};

export default GuidingArrow;
